import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { useSiteData } from "@/context/SiteDataContext";
import { waLink } from "@/lib/api";
import { LANDINGS } from "@/lib/landing";
import { PageHero, SectionHead } from "@/components/site/PageHero";
import { PackageCard } from "@/components/site/PackageCard";
import { EnquiryDialog } from "@/components/site/EnquiryDialog";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { SITE_URL, Seo, breadcrumb } from "@/components/site/Seo";

const ldFor = (c, list) => [
  breadcrumb([["Home", "/"], ["Tour Packages", "/packages"], [c.eyebrow, c.path]]),
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: c.faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
  { "@context": "https://schema.org", "@type": "ItemList", name: c.eyebrow, itemListElement: list.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.name, url: `${SITE_URL}/packages/${p.slug}` })) },
];

const LeadBlock = ({ c, wa, kind }) => (
  <section id="quote" className="container-x pt-20 scroll-mt-24" data-testid={`landing-${kind}-quote-section`}>
    <div className="rounded-3xl bg-navy p-6 sm:p-10 lg:p-14 grid gap-10 lg:grid-cols-12">
      <div className="lg:col-span-5 text-white">
        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-aqua">Free quote</p>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold">Get your {c.category.toLowerCase()} package quote</h2>
        <ul className="mt-6 space-y-3">{c.perks.map((p) => <li key={p} className="flex gap-3 text-slate-200"><Check className="h-5 w-5 text-aqua shrink-0" />{p}</li>)}</ul>
        <a href={wa} target="_blank" rel="noreferrer" data-testid={`landing-${kind}-form-whatsapp`}
          className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-500 font-semibold hover:bg-emerald-600 transition-colors"><WhatsAppIcon className="h-5 w-5" /> WhatsApp Enquiry</a>
      </div>
      <div className="lg:col-span-7 rounded-2xl bg-white p-6 sm:p-8"><EnquiryForm idPrefix={`landing-${kind}`} defaultTripType={c.category} /></div>
    </div>
  </section>
);

const Faq = ({ c, kind }) => (
  <section className="container-x pt-20 grid gap-8 lg:grid-cols-12" data-testid={`landing-${kind}-faq`}>
    <div className="lg:col-span-4"><p className="eyebrow">FAQ</p><h2 className="mt-3 font-display text-4xl text-navy font-semibold">{c.eyebrow}: common questions</h2></div>
    <Accordion type="single" collapsible className="lg:col-span-8">
      {c.faqs.map(([q, a], i) => (
        <AccordionItem key={q} value={`f-${i}`}><AccordionTrigger className="text-left text-base font-semibold text-navy" data-testid={`landing-${kind}-faq-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent></AccordionItem>
      ))}
    </Accordion>
  </section>
);

export default function CategoryLanding({ kind }) {
  const c = LANDINGS[kind];
  const { packages, loading, settings } = useSiteData();
  const list = packages.filter((p) => p.category === c.category || p.alt_categories?.includes(c.category));
  const wa = waLink(settings.whatsapp, `Hi Ocean Wings Travel, I'd like a quote for an Andaman ${c.category} package. Travel date: , Adults: , Children: `);
  return (
    <div data-testid={`landing-${kind}-page`}>
      <Seo title={c.seoTitle} description={c.seoDesc} keywords={c.keywords} path={c.path} image={list[0]?.image} jsonLd={ldFor(c, list)} />
      <PageHero img={c.img} alt={c.alt} eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} testId={`landing-${kind}-hero`}>
        <EnquiryDialog tripType={c.category} title={`Free ${c.category} package quote`} idPrefix={`landing-${kind}-dialog`}
          trigger={<button type="button" data-testid={`landing-${kind}-hero-quote`} className="h-12 px-6 rounded-full bg-white text-navy font-semibold hover:bg-sky-50 transition-colors">Get Free Quote</button>} />
        <a href={wa} target="_blank" rel="noreferrer" data-testid={`landing-${kind}-hero-whatsapp`}
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors"><WhatsAppIcon className="h-5 w-5" /> WhatsApp Enquiry</a>
      </PageHero>
      <section className="container-x pt-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{c.perks.map((p) => <p key={p} className="flex items-start gap-2 rounded-2xl border border-sky-100 bg-mist p-4 text-sm font-medium text-navy"><Check className="h-4 w-4 text-ocean shrink-0 mt-0.5" />{p}</p>)}</div>
      </section>
      <section className="container-x pt-16" data-testid={`landing-${kind}-packages`}>
        <SectionHead eyebrow={`${list.length || ""} ${c.category} packages`} title={`Choose your ${c.category.toLowerCase()} itinerary`} subtitle="2N/3D to 6N/7D, each with a day-wise plan, hotel stay, ferries and transfers.">
          <Link to={`/packages?category=${c.category}`} className="text-sm font-semibold text-ocean hover:text-navy" data-testid={`landing-${kind}-all-link`}>Compare all packages</Link>
        </SectionHead>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? [1, 2, 3].map((i) => <Skeleton key={i} className="h-[440px] rounded-2xl" />) : list.map((p) => <PackageCard key={p.id} pkg={p} />)}
        </div>
      </section>
      <LeadBlock c={c} wa={wa} kind={kind} />
      <Faq c={c} kind={kind} />
    </div>
  );
}
